import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadSimple, Trash } from '@phosphor-icons/react';
import { CompanyLogoType } from '@monorepo/shared';
import type { CompanyDto } from '@monorepo/shared';
import { useUploadCompanyLogo, useDeleteCompanyLogo } from '../../services/company.service';

const MAX_LOGO_SIZE = 10 * 1024 * 1024;
const ALLOWED_LOGO_MIME_TYPES = ['image/jpeg', 'image/png'];

interface CompanyLogoUploadProps {
  companyId: string;
  type: CompanyLogoType;
  existingUrl?: string;
}

const CompanyLogoUpload = ({ companyId, type, existingUrl }: CompanyLogoUploadProps) => {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadCompanyLogo(companyId);
  const deleteMutation = useDeleteCompanyLogo(companyId);

  const validateAndUpload = useCallback(
    async (file: File) => {
      setFileError(null);

      if (!ALLOWED_LOGO_MIME_TYPES.includes(file.type)) {
        setFileError(t('company.logo.upload_error.invalid_format'));
        return;
      }

      if (file.size > MAX_LOGO_SIZE) {
        setFileError(t('company.logo.upload_error.file_too_large'));
        return;
      }

      await uploadMutation.mutateAsync({ type, file });
    },
    [uploadMutation, type, t],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        void validateAndUpload(file);
      }
    },
    [validateAndUpload],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void validateAndUpload(file);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [validateAndUpload],
  );

  const handleDelete = useCallback(async () => {
    await deleteMutation.mutateAsync(type);
  }, [deleteMutation, type]);

  const isLoading = uploadMutation.isPending || deleteMutation.isPending;
  const labelKey =
    type === CompanyLogoType.FULL ? 'company.logo.full_label' : 'company.logo.square_label';

  if (existingUrl) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
        <div className="flex items-center gap-4 p-3 border border-gray-200 rounded-md bg-gray-50">
          <img src={existingUrl} alt={t(labelKey)} className="h-16 object-contain rounded" />
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash size={16} />
            {t('company.logo.delete_button')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-gray-700">{t(labelKey)}</span>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          flex flex-col items-center justify-center gap-3 p-6
          border border-dashed rounded-md transition-colors
          ${isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300 bg-white'}
        `}
      >
        <UploadSimple size={36} className="text-gray-400" />
        <p className="text-xs text-gray-500 text-center">{t('company.logo.upload_hint')}</p>

        {fileError && <p className="text-xs text-red-600">{fileError}</p>}
        {uploadMutation.isError && <p className="text-xs text-red-600">{t('common.error')}</p>}

        <label className="cursor-pointer">
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            onChange={handleInputChange}
            className="hidden"
            disabled={isLoading}
          />
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-900 text-sm font-medium rounded-md hover:bg-gray-200 transition shadow-[0px_2px_10px_0px_rgba(0,41,102,0.08)]">
            {isLoading ? t('common.loading') : t('company.logo.import_button')}
          </span>
        </label>
      </div>
    </div>
  );
};

interface CompanyLogoSectionProps {
  company: CompanyDto;
}

export const CompanyLogoSection = ({ company }: CompanyLogoSectionProps) => {
  return (
    <div className="flex flex-col gap-6">
      <CompanyLogoUpload
        companyId={company.id}
        type={CompanyLogoType.FULL}
        {...(company.logoFullUrl ? { existingUrl: company.logoFullUrl } : {})}
      />
      <CompanyLogoUpload
        companyId={company.id}
        type={CompanyLogoType.SQUARE}
        {...(company.logoSquareUrl ? { existingUrl: company.logoSquareUrl } : {})}
      />
    </div>
  );
};
